import { useSelector } from 'react-redux';
import React, { useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router';

import { useGetPartnerDetails } from 'hooks/useGetPartnerDetails';
import { GlobalState } from 'reducers/stateTypes';
import { ContactNoteDialog, ContactNoteFromEmail } from 'types/ContactNote';
import { selectIsUsedInBr } from 'selectors/contactNoteListSelectors';
import { User } from 'types/User';
import { decodeBase64ToBlob } from 'utils/noteDialogUtil';
import { ObjectSchema } from 'yup';
import { FileInfo } from 'views/ContactNoteForm/components/DocumentUpload/DocumentDropZone';
import { LinearProgress } from '@mui/material';

import { UserPartnerType } from './data/UserPartnerStatus';
import contactNoteDialogValidationSchema from './contactNoteDialogValidationSchema';
import { ContactNoteInitiatedUiScreen } from './data/ContactNoteDialogStatus';

type WithEditProp = {
  initialValues: ContactNoteDialog;
  validationSchema: ObjectSchema<any>;
  onClose?: () => void;
};

const withInitialValuesFromEmailContactNotes = <P extends object>(
  WrappedComponent: React.ComponentType<P & WithEditProp>,
): React.FC<Omit<P, keyof WithEditProp>> => {
  const ComponentWithInitialValues = (props: Omit<P, keyof WithEditProp>) => {
    const { ...restProps } = props;
    const { techId } = useParams<{ techId: string }>();
    const usedInBr = useSelector(selectIsUsedInBr);

    const user: User = useSelector((state: GlobalState) => state.user.userDetails);
    const { data: partnerDetails } = useGetPartnerDetails(techId || '', usedInBr);
    const [contactNoteEmailData, setContactNoteEmailData]: any = useState(undefined);
    const initiatedByOptions = useSelector((state: GlobalState) => state.resources.initiatedByOptions);

    const initialValues = (data: ContactNoteFromEmail, user: User) => {
      return {
        partner: {
          globalPartnerId: '',
          partnerUId: data?.partner?.partnerUId || '',
          fullName: '',
          relationshipManager: {
            uid: user.username,
            fullName: `${user.givenName} ${user.sureName}`,
          },
        },
        additionalAttendees: [user.username],
        body: data.body,
        addEmailToPartner: false,
        direction: undefined,
        qualifiedClientContact: undefined,
        crossBorderActivity: 'false',
        subject: data.subject,
        contactType: 'EMAIL',
        endDateTime: '',
        topics: data?.topics || [],
        involvedRoles: [],
        initiatedBy: data?.initiatedBy || '',
        notes: data.body || '',
        createdBy: user.username,
        attachments: data.attachments || [],
        status: 'EDITABLE',
        email: {
          subject: data?.subject,
          from: data?.from,
          to: data?.to,
          cc: data?.cc,
          bcc: data?.bcc,
          body: data?.body,
          startDateTime: data?.startDateTime || '',
        },
        locationCountry: '',
        locationCity: '',

        createdDateTime: null,
        globalPartnerId: '',
        source: '',
        tripId: '',
        updateDateTime: null,

        relatedPartners: [],
        otherPartner: '',
        otherBR: '',
        relatedBRs: [],
        topicOther: '',
        notifyOtherUser: [user.username],
        contactTypeOther: '',
        isTimeAllowed: false,
        br: {
          relatedBrUid: null,
        },
        startDate: null,
        startTime: null,
        endDate: null,
        endTime: null,
        dragAndDrop: true,
        initiatedUiScreen: ContactNoteInitiatedUiScreen.DRAGANDDROP,
      };
    };

    useEffect(() => {
      const handleMessage = async (event: MessageEvent) => {
        if (event?.data?.from) {
          let data = event.data;
          if (data.attachments.length > 0) {
            const attachmentBlobs = await Promise.all(
              ((data.attachments as FileInfo[]) ?? []).map(async (attachment) => {
                if (!attachment.b64Data) return null;
                const blob = await decodeBase64ToBlob(attachment.b64Data);
                return { ...attachment, file: blob };
              }),
            );
            data = { ...data, attachments: attachmentBlobs };
          }
          setContactNoteEmailData(initialValues(data, user));
        }
      };
      window.addEventListener('message', handleMessage);

      window.parent.postMessage(
        {
          message: 'sendContactNoteData',
        },
        '*',
      );

      return () => {
        window.removeEventListener('message', handleMessage);
      };
    }, [user]);

    const postCloseWindowMessage = () =>
      window.parent.postMessage(
        {
          message: 'closeContactNoteModal',
        },
        '*',
      );

    const isLE = useMemo(() => {
      if (partnerDetails) {
        return partnerDetails.partnerStructureType === 'LEGAL_ENTITY_PARTNER';
      }
      return false;
    }, [partnerDetails]);

    const isPartner = useMemo(() => {
      if (partnerDetails) {
        if (partnerDetails.partnerStructureType === 'INDIVIDUAL_PARTNER') {
          return partnerDetails.individualPartner.status === UserPartnerType.ONBOARDED;
        } else if (partnerDetails.partnerStructureType === 'LEGAL_ENTITY_PARTNER') {
          return partnerDetails.legalPartner.status === UserPartnerType.ONBOARDED;
        }
      }

      return false;
    }, [partnerDetails]);

    const validationSchema = useMemo(() => {
      return contactNoteDialogValidationSchema(usedInBr, isPartner, isLE, true);
    }, [usedInBr, isPartner, isLE]);

    return contactNoteEmailData && initiatedByOptions.length > 0 ? (
      <WrappedComponent
        {...(restProps as P)}
        initialValues={contactNoteEmailData}
        edit={false}
        validationSchema={validationSchema}
        onClose={postCloseWindowMessage}
        isDragAndDrop={true}
      />
    ) : (
      <LinearProgress id="note-from-email-loading" />
    );
  };

  return ComponentWithInitialValues;
};

export default withInitialValuesFromEmailContactNotes;
